import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getSuperAdminClaimBusinesses,
  PaginatedResponse,
  SuperAdminClaimBusiness,
} from "../../services/superAdminApi";

const statusBadge = (value?: string | null) => {
  const claimed = (value || "").toLowerCase() === "yes";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
        claimed ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {claimed ? "Yes" : "No"}
    </span>
  );
};

const SuperAdminClaimBusinesses: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SuperAdminClaimBusiness[]>([]);
  const [pagination, setPagination] =
    useState<PaginatedResponse<SuperAdminClaimBusiness> | null>(null);
  const [search, setSearch] = useState("");
  const [perPage, setPerPage] = useState(15);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRows = async (page = 1, overrideSearch?: string) => {
    try {
      setLoading(true);
      setError("");
      const res = await getSuperAdminClaimBusinesses({
        page,
        per_page: perPage,
        search: (overrideSearch ?? search) || undefined,
      });
      setRows(res.data || []);
      setPagination(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load claimed businesses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;
  const total = pagination?.total ?? rows.length;

  return (
    <div className="space-y-5 p-1">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#402B69]">Claimed Businesses</h1>
          <p className="text-sm text-gray-500">
            Web-matched claimed companies list inside Glossa.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/superadmin/companies")}
          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700"
        >
          Back to Companies
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[240px]">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, email, address..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => fetchRows(1)}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => {
            setSearch("");
            fetchRows(1, "");
          }}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Reset
        </button>
        <div className="ml-auto rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
          Total Claimed: {total}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-600">Loading claimed businesses...</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Email</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Address</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-gray-500">
                        No claimed businesses found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                        <td className="px-4 py-3 text-gray-700">
                          {(currentPage - 1) * perPage + index + 1}
                        </td>
                        <td className="px-4 py-3 font-medium text-[#402B69]">
                          {row.company_name || "-"}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{row.company_email || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.address || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">{statusBadge(row.is_claim)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Showing page {currentPage} of {lastPage} ({total} total records)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchRows(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchRows(currentPage + 1)}
                  disabled={currentPage >= lastPage}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdminClaimBusinesses;
